import { Autocomplete, TextField } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { useContext, useEffect, useState } from 'react';

import { Context } from '../../service/Context';
import VirtualizedListbox from '../../utils/UtilesBuscadores';
import { capitalizar } from '../../utils/UtilesComun';

/**
 * Selector de club reutilizable, con soporte para:
 * - Selección de clubes existentes de la base de datos (virtualizado).
 * - Escritura libre de clubes no registrados (freeSolo).
 *
 * @param {string}   value       Valor actual del club (texto).
 * @param {function} onChange    Callback cuando cambia el valor: (nuevoValor: string) => void.
 * @param {string}   placeholder Texto placeholder del input.
 * @param {object}   sx          Estilos adicionales para el TextField.
 * @param {object}   InputProps  Props adicionales para el InputProps del TextField.
 */
const SelectorClub = ({
  value = '',
  onChange,
  onSelect,
  onBlur,
  placeholder = 'Busca o escribe club...',
  sx,
  InputProps,
}) => {
  const { idUsuario, obtenerTodosClubes } = useContext(Context);
  const [todosClubes, setTodosClubes] = useState([]);

  useEffect(() => {
    async function cargarClubes() {
      if (idUsuario) {
        const clubes = await obtenerTodosClubes();
        setTodosClubes(clubes);
      }
    }
    cargarClubes();
  }, [idUsuario, obtenerTodosClubes]);

  const getLabel = (option) => {
    let label = '';
    if (typeof option === 'string') {
      label = option;
    } else {
      label = option.second || '';
    }
    return capitalizar(label);
  };

  return (
    <Autocomplete
      freeSolo
      options={todosClubes}
      getOptionLabel={getLabel}
      ListboxComponent={VirtualizedListbox}
      filterOptions={(options, params) => {
        const filter = createFilterOptions({
          stringify: (option) => getLabel(option),
        });
        const filtered = filter(options, params);

        const { inputValue } = params;
        if (inputValue !== '') {
          const inputNorm = inputValue.toLowerCase().trim();
          const isExisting = options.some((option) => getLabel(option).toLowerCase() === inputNorm);
          if (!isExisting) {
            filtered.push(inputValue);
          }
        }
        return filtered;
      }}
      onInputChange={(event, newInputValue) => {
        onChange(newInputValue);
      }}
      onChange={(event, newValue) => {
        if (onSelect) {
          const val = typeof newValue === 'string' ? newValue : newValue?.second || '';
          onSelect(val);
        }
      }}
      onBlur={onBlur}
      value={value}
      renderOption={(props, option) => [props, getLabel(option)]}
      componentsProps={{ popper: { style: { minWidth: '20%', width: 'auto' } } }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            ...(InputProps || {}),
            sx: { fontSize: '0.85rem', ...(InputProps?.sx || {}) },
          }}
          sx={{ minWidth: 160, mx: 1, ...(sx || {}) }}
        />
      )}
    />
  );
};

export default SelectorClub;
